import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  User,
  Printer,
  CalendarClock,
  Box,
  Layers,
  Copy,
  Maximize
} from "lucide-react";
import { adminApiRequest } from "../utils/adminApi";
import Modal from "./Modal";

export default function PrintOrders() {
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [markingReadyId, setMarkingReadyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [orderToConfirm, setOrderToConfirm] = useState(null);

  const visibleOrders = useMemo(() => {
    return [...orders].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [orders]);

  const formatDateTime = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleExpanded = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "fetch_print_orders",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load orders.");
      }

      setOrders(Array.isArray(res.orders) ? res.orders : []);
    } catch (err) {
      setError(err.message || "Unable to load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkReady = async (orderId) => {
    try {
      setMarkingReadyId(orderId);
      setError("");
      setSuccessMessage("");
      setOrderToConfirm(null);

      const res = await adminApiRequest({
        script: "mark_print_order_completed",
        data: { order_id: orderId },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update order.");
      }

      await loadOrders();

      setSuccessMessage("Order marked as ready.");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      setError(err.message || "Unable to mark order as completed.");
    } finally {
      setMarkingReadyId(null);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  return (
    <section className="h-full min-h-0">
      {orderToConfirm && (
        <Modal
          title="Confirm Ready Status"
          onClose={() => setOrderToConfirm(null)}
          onSubmit={(e) => {
            e.preventDefault();
            handleMarkReady(orderToConfirm.id);
          }}
          submitButtonText="Yes, Mark as Ready"
          submitButtonClass="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
          showCancel={true}
          showSubmitButton={true}
        >
          <div className="space-y-3 py-2">
            <p className="text-slate-600">
              Are you sure you want to mark order <strong className="text-slate-900">{orderToConfirm.order_ref}</strong> as ready?
            </p>
            <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Customer</div>
              <div className="text-sm font-semibold text-slate-700">{orderToConfirm.full_name}</div>
            </div>
            <p className="text-xs text-slate-400 italic">
              This will notify the customer (if enabled) and move the order to the completed list.
            </p>
          </div>
        </Modal>
      )}

      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-5">
          <h2 className="text-2xl font-semibold text-slate-900">Print Orders</h2>
          <p className="mt-1 text-sm text-slate-500">
            View and manage printing requests.
          </p>
        </div>

        {error && (
          <div className="px-5 pt-4">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}

        {successMessage && (
          <div className="px-5 pt-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {successMessage}
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading orders...
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">No active print orders.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {visibleOrders.map((order) => {
                const isExpanded = expandedOrderId === order.id;
                const status = order.status || "pending";

                const statusConfig = {
                  pending: {
                    label: "Pending",
                    className: "bg-yellow-100 text-yellow-700",
                  },
                  processing: {
                    label: "Processing",
                    className: "bg-blue-100 text-blue-700 animate-pulse",
                  },
                  completed: {
                    label: "Ready",
                    className: "bg-emerald-100 text-emerald-700",
                  },
                  cancelled: {
                    label: "Cancelled",
                    className: "bg-red-100 text-red-700",
                  },
                };

                const currentStatus = statusConfig[status] || {
                  label: status || "Unknown",
                  className: "bg-slate-100 text-slate-700",
                };

                const isReady = status === "completed";
                
                return (
                  <div
                    key={order.id}
                    onClick={() => toggleExpanded(order.id)}
                    className={`cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition ${
                      isReady
                        ? "border-emerald-200 bg-emerald-50/70 ring-1 ring-emerald-100"
                        : "border-slate-200 bg-white hover:shadow-md"
                    }`}
                  >
                    <div className="p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-slate-500">
                          <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {formatDateTime(order.created_at)}
                          </span>
                        </div>

                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${currentStatus.className}`}
                        >
                          {currentStatus.label}
                        </span>
                      </div>

                      {isReady && (
                        <div className="mb-3 inline-flex items-center rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                          Ready for Collection
                        </div>
                      )}

                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-base font-semibold text-slate-900">
                            {order.order_ref}
                          </div>

                          <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
                            <User className="h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {order.full_name}
                            </span>
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          <div className="text-lg font-semibold text-slate-900">
                            £{Number(order.total_price || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {isReady && order.ready_at && (
                        <div className="mt-3 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-emerald-700">
                          Ready at:{" "}
                          <span className="font-semibold">
                            {formatDateTime(order.ready_at)}
                          </span>
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderToConfirm(order);
                          }}
                          disabled={isReady || markingReadyId === order.id}
                          className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white transition ${
                            isReady || markingReadyId === order.id
                              ? "cursor-not-allowed bg-slate-400"
                              : "bg-emerald-600 hover:bg-emerald-700"
                          }`}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          <span>
                            {markingReadyId === order.id
                              ? "Updating..."
                              : isReady ? "Ready" : "Mark as Ready"}
                          </span>
                        </button>

                        <a
                          href={`tel:${order.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 text-slate-700 transition hover:bg-slate-50"
                          title="Call Customer"
                        >
                          <Phone className="h-4 w-4" />
                        </a>

                        <a
                          href={`mailto:${order.email}?subject=${encodeURIComponent(
                            `Update on your print order ${order.order_ref}`,
                          )}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 text-slate-700 transition hover:bg-slate-50"
                          title="Email Customer"
                        >
                          <Mail className="h-4 w-4" />
                        </a>

                        <button
                          type="button"
                          className="ml-auto inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                        >
                          <span>{isExpanded ? "Less" : "Details"}</span>
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 border-t border-slate-100 pt-4 space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-xl bg-slate-50 p-3 border border-slate-100">
                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                    <Printer size={12} /> Category
                                </div>
                                <div className="text-sm font-semibold text-slate-800">{order.category}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 border border-slate-100">
                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                    <Maximize size={12} /> Size/Option
                                </div>
                                <div className="text-sm font-semibold text-slate-800">{order.sub_label}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 border border-slate-100">
                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                    <Layers size={12} /> Variation
                                </div>
                                <div className="text-sm font-semibold text-slate-800">{order.group_label}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 border border-slate-100">
                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                                    <Copy size={12} /> Sides
                                </div>
                                <div className="text-sm font-semibold text-slate-800 uppercase">{order.sided}</div>
                            </div>
                          </div>

                          <div className="rounded-xl bg-indigo-50 p-3 border border-indigo-100">
                            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-indigo-400 mb-1">
                                <Box size={12} /> Quantity
                            </div>
                            <div className="text-lg font-bold text-indigo-700">{order.qty} <span className="text-sm font-medium">units</span></div>
                          </div>

                          {order.extras && order.extras.length > 0 && (
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 px-1">Selected Extras</div>
                                <div className="flex flex-wrap gap-2">
                                    {order.extras.map((ex, i) => (
                                        <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-xs font-semibold text-slate-600 border border-slate-200">
                                            {ex.name} (+£{Number(ex.price).toFixed(2)})
                                        </span>
                                    ))}
                                </div>
                            </div>
                          )}


                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
